import { type Heat, type Member, type Tournament } from "../api";
import { MemberAvatar } from "../avatar";

/**
 * Who predicted a vehicle, and how that is shown. Requirement 9.9 — **avatars only, never names.**
 *
 * Its own module because two screens need it and the logic is requirement-9.4-sensitive: a copy that
 * drifted could leak an unrevealed pick, which is the one thing 9.4 exists to prevent.
 *
 * **The protection is the server's, not this file's.** `scoring.visible_picks` sends everyone's picks
 * for revealed rounds plus your own and nothing else, so `tournament.picks` cannot contain a pick you
 * are not entitled to see. That is why there is no round-state check here — a client-side rule would be
 * a second place for the answer to live, and the weaker of the two.
 *
 * What that gives, in the room: a round reveals when its first race is called, which is also the moment
 * picks lock (9.5). So during a round the avatars appear beside each vehicle as phones lock in, and the
 * *next* round's picks stay invisible until it opens. Nobody copies, and nobody has to be told not to.
 */
export function backersOf(tournament: Tournament, heat: Heat, vehicleId: string): string[] {
  return tournament.picks
    .filter((pick) => pick.round === heat.round && (pick.advancers ?? []).includes(vehicleId))
    .map((pick) => pick.sub);
}

/** The avatar stack itself, sized to sit inside a `.lane` without changing its height. */
export function PickedBy({ subs, members }: { subs: string[]; members: Member[] }) {
  if (subs.length === 0) return null;
  const bySub = new Map(members.map((member) => [member.sub, member]));
  return (
    <span className="avatars">
      {/* No photograph, by omission rather than oversight: neither the roster nor a pick carries one, so
          `MemberAvatar` falls back to the hue-and-shape identity derived from the subject id. That is
          deliberate — the same derivation runs on the television, and `avatar.test.ts` and
          `MemberAvatarTests.swift` assert the two agree — so a person is the same small symbol in both
          places. Recognising people by symbol rather than by name is the point of 9.9. */}
      {subs.slice(0, 4).map((sub) => (
        <MemberAvatar key={sub} sub={sub} size={24} name={bySub.get(sub)?.name} />
      ))}
      {subs.length > 4 && <span className="muted">+{subs.length - 4}</span>}
    </span>
  );
}
